/**
 * Reset Logto's locally cached session state. Called from the
 * auth-loading watchdog recovery path when the SDK appears stuck on
 * rehydrate (e.g., a `/oidc/token` refresh grant that never resolves, a
 * stale half-finished promise persisted across a tab sleep, or simply a
 * corrupt entry from a prior SDK major). Wipes the `logto:*` localStorage
 * keys the SDK uses for tokens, sign-in state, and PKCE verifiers so the
 * next provider mount starts with a clean slate.
 *
 * Safe to call at any time:
 *  - Uses prefix matching so we never touch unrelated app storage.
 *  - Swallows every storage exception (private browsing, quota, locked
 *    profile) — the caller's hard-reload-to-/login still recovers.
 */
export function clearLogtoSessionStorage(): void {
  if (typeof window === 'undefined') return
  let store: Storage
  try {
    store = window.localStorage
  } catch {
    return
  }

  const keysToRemove: string[] = []
  try {
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i)
      if (key && key.startsWith('logto:')) {
        keysToRemove.push(key)
      }
    }
  } catch (err) {
    console.warn('clearLogtoSessionStorage: enumeration failed', err)
    return
  }

  for (const key of keysToRemove) {
    try {
      store.removeItem(key)
    } catch {
      // Per-key quota/locked errors are non-fatal — keep going.
    }
  }
}

/**
 * Indirection over window.location.assign so the recovery path is unit-
 * testable in jsdom (which marks `location.assign` non-configurable and
 * refuses to let tests defineProperty over it). The setter is intended
 * for tests only.
 */
let hardNavigateImpl: (url: string) => void = (url) => {
  if (typeof window !== 'undefined') {
    window.location.assign(url)
  }
}

export function __setHardNavigateForTests(
  impl: (url: string) => void,
): () => void {
  const previous = hardNavigateImpl
  hardNavigateImpl = impl
  return () => {
    hardNavigateImpl = previous
  }
}

/**
 * Recovery action used by the loading-watchdog UI: drop the cached Logto
 * session, then hard-navigate to /login with a `reason` so the login
 * surface can show a "your session expired" banner. We deliberately use
 * `window.location.assign` (not the SPA router) so the LogtoProvider is
 * fully unmounted — an in-SPA navigation would keep the same stuck
 * provider mounted and we'd loop right back into the same spinner.
 */
export function recoverFromStuckAuth(reason: string = 'session_timeout'): void {
  clearLogtoSessionStorage()
  hardNavigateImpl(`/login?reason=${encodeURIComponent(reason)}`)
}
